import React, { useEffect, useState } from 'react';
import { fetchCrops, fetchDiseases, fetchSymptomsByDisease } from '../api/cropService';
import { useTranslation } from 'react-i18next';

const ViewProblem = () => {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [diseases, setDiseases] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initial crop load
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const cropData = await fetchCrops(true);
        setCrops(cropData);
        if (cropData.length > 0) {
          const firstCrop = cropData[0];
          setSelectedCrop(firstCrop);
        }
      } catch (err) {
        console.error('Error loading crops:', err);
      }
    };
    loadInitialData();
  }, []);

  // Load diseases when crop changes
  useEffect(() => {
    const loadDiseases = async () => {
      if (!selectedCrop) return;
      setLoading(true);
      try {
        const diseaseData = await fetchDiseases(selectedCrop.cid, true);
        setDiseases(diseaseData);
        if (diseaseData.length > 0) {
          setSelectedDisease(diseaseData[0]); // select first disease by default
        } else {
          setSelectedDisease(null);
        }
        setSymptoms([]);
      } catch (err) {
        console.error('Error fetching diseases:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDiseases();
  }, [selectedCrop]);

  // Load symptoms when disease changes
  useEffect(() => {
    const loadSymptoms = async () => {
      if (!selectedDisease) return;
      setLoading(true);
      try {
        const symptomData = await fetchSymptomsByDisease(selectedDisease.disId, true);
        setSymptoms(symptomData);
      } catch (err) {
        console.error('Error fetching symptoms:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSymptoms();
  }, [selectedDisease]);

  return (
    <div className="flex h-screen">
      {/* LEFT: Disease Details */}
      <div className="w-2/3 p-6 overflow-y-auto bg-gray-50">
        {loading ? (
          <p className="text-gray-500">{t('expert.viewProblem.loading')}</p>
        ) : selectedDisease ? (
          <>
            <h1 className="text-2xl font-bold mb-4 border-b pb-2 text-gray-800">{t('expert.viewProblem.pageTitle')}</h1>
            <div className="grid grid-cols-3 gap-4 text-gray-700 mb-6">
              <div className="font-semibold">{t('expert.viewProblem.commonName')}</div>
              <div className="col-span-2">{selectedDisease.chemical_name}</div>

              <div className="font-semibold">{t('expert.viewProblem.scientificName')}</div>
              <div className="col-span-2">{selectedDisease.scientific_name}</div>

              <div className="font-semibold">{t('expert.viewProblem.description')}</div>
              <div className="col-span-2">{selectedDisease.scientific_name}</div>

              <div className="font-semibold">{t('expert.viewProblem.chemicalAdvice')}</div>
              <div className="col-span-2">{selectedDisease.chemicalAdvice}</div>

              <div className="font-semibold">{t('expert.viewProblem.organicAdvice')}</div>
              <div className="col-span-2">{selectedDisease.organiceAdvice}</div>
            </div>
            <h2 className="text-xl font-semibold mb-3">{t('expert.viewProblem.symptomsTitle')}</h2>
            {symptoms.length === 0 ? (
              <p className="text-gray-600">{t('expert.viewProblem.noSymptomsFound')}</p>
            ) : (
              <ul className="space-y-4">
                {symptoms.map(sym => (
                  <li key={sym.sid} className="bg-white border p-4 rounded shadow-sm">
                    <p className="text-gray-700">{t('expert.viewProblem.symptomNameLabel')} {sym.name}</p>
                    <p className="text-gray-700">{t('expert.viewProblem.questionLabel')} {sym.qn}</p>
                    <p className="text-gray-700">{t('expert.viewProblem.symptomLevelLabel')} {sym.level}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="text-gray-500">{t('expert.viewProblem.selectDiseasePrompt')}</p>
        )}
      </div>

      {/* RIGHT: Crops and Diseases */}
      <div className="w-1/3 p-6 border-l border-gray-200 overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{t('expert.viewProblem.existingCropsTitle')}</h2>
        <ul>
          {crops.map(crop => (
            <li key={crop.cid}>
              <div
                onClick={() => setSelectedCrop(crop)}
                className={`cursor-pointer p-3 rounded mb-2 transition font-medium text-blue-400 ${
                  selectedCrop?.cid === crop.cid ? 'bg-blue-600 text-blue-200' : 'hover:bg-gray-100'
                }`}
              >
                {crop.name}
              </div>
            </li>
          ))}
        </ul>

        {/* Display diseases below the crop section */}
        {selectedCrop && diseases.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">{t('expert.viewProblem.diseasesTitle')}</h2>
            <ul>
              {diseases.map(disease => (
                <li
                  key={disease.disId}
                  onClick={() => setSelectedDisease(disease)}
                  className={`cursor-pointer p-2 my-1 rounded text-sm text-blue-400 ${
                    selectedDisease?.disId === disease.disId
                      ? 'bg-blue-600 text-blue-200'
                      : 'hover:bg-gray-200'
                  }`}
                >
                  {disease.scientific_name || disease.chemical_name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewProblem;